// PersonalInformation.jsx
import React from "react";
import {
  TextField,
  Autocomplete,
  Box,
  Typography,
  Divider,
  Paper,
  InputAdornment,
} from "@mui/material";
import countryCodes from "./countryCode";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';

export default function PersonalInformation({
  form,
  selectedCountry,
  setSelectedCountry,
  handleCountrySelect,
  handleChange,
  countryData,
  cityList,
  userInfo,
  handleCitySelect,
  errors,
  setErrors,
  disabled,
}) {
  const inputStyles = {
    mb: 2,
    "& label": {
      color: "#343a40",
      fontWeight: 600,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#343a40" },
      "&:hover fieldset": { borderColor: "#000" },
      "&.Mui-focused fieldset": { borderColor: "#000" },
    },
  };

  const capitalize = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const toggleShowPassword = () => setShowPassword((prev) => !prev);
  const toggleShowConfirmPassword = () => setShowConfirmPassword((prev) => !prev);
    
  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;
    return regex.test(password);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 1,
        borderRadius: 3,
        backgroundColor: "#fafafa",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.08)",
        maxWidth: 900,
        width: "100%",
        mx: "auto",
      }}
      component="section"
      aria-labelledby="personal-info-header"
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "#222",
          mb: 2,
          textAlign: "left",
        }}
        id="personal-info-header"
      >
        Personal Information
      </Typography>

      <Divider sx={{ mb: 4 }} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, sm: 3 },
        }}
      >
        
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            required
            label="First Name"
            name="firstname"
            value={capitalize(form.firstname)}
            onChange={handleChange}
            error={Boolean(errors.firstname)}
            helperText={errors.firstname}
            sx={inputStyles}
            autoComplete="given-name"
            inputProps={{ "aria-label": "First Name" }}
          />
          <TextField
            fullWidth
            required
            label="Last Name"
            name="lastname"
            value={capitalize(form.lastname)}
            onChange={handleChange}
            error={Boolean(errors.lastname)}
            helperText={errors.lastname}
            sx={inputStyles}
            autoComplete="family-name"
            inputProps={{ "aria-label": "Last Name" }}
          />
        </Box>

        
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            required
            label="Email Address"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            error={Boolean(errors.email)}
            helperText={errors.email}
            sx={inputStyles}
            autoComplete="email"
            inputProps={{ "aria-label": "Email Address" }}
          />

          <TextField
            fullWidth
            required
            label="Phone Number"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={(e) => {
              // Hinahayaan lang natin ang pure typing nang walang kahalong format logic o error trigger
              handleChange(e);
              setErrors((prev) => ({ ...prev, phone: false }));
            }}
            error={Boolean(errors.phone)}
            helperText={errors.phone || " "}
            sx={inputStyles}
            autoComplete="tel"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1, minWidth: 120 }}>
                  <Autocomplete
                    disableClearable
                    options={countryCodes}
                    getOptionLabel={(option) => `${option.flag} (${option.code})`}
                    value={
                      countryCodes.find((c) => c.code === form.country_code) ||
                      countryCodes.find((c) => c.code === "+63")
                    }
                    onChange={(e, newValue) => {
                      const newCode = newValue?.code || "+63";
                      const selectedCountryObj = countryCodes.find((c) => c.code === newCode);

                      handleChange({ target: { name: "country_code", value: newCode } });

                      if (setSelectedCountry) {
                        setSelectedCountry({
                          name: selectedCountryObj?.name || "",
                          flag: selectedCountryObj?.flag || "",
                          dial_code: newCode 
                        });
                      }

                      setErrors((prev) => ({ ...prev, phone: false }));
                    }}
                    size="small"
                    sx={{
                      width: 120,
                      "& .MuiOutlinedInput-root": {
                        padding: 0,
                        "& fieldset": { border: "none" },
                        backgroundColor: "transparent",
                      },
                      "& .MuiAutocomplete-inputRoot": {
                        padding: "0 8px",
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        size="small"
                        sx={{
                          "& .MuiInputBase-input": {
                            textAlign: "center",
                            fontWeight: 600,
                        },
                        }}
                        inputProps={{
                          ...params.inputProps,
                          style: { padding: 0, textAlign: "center" },
                        }}
                      />
                    )}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        
        <TextField
          fullWidth
          required
          multiline
          minRows={2}
          label="Address Line 1"
          name="address"
          value={form.address}
          onChange={handleChange}
          error={Boolean(errors.address)}
          helperText={errors.address}
          sx={inputStyles}
          inputProps={{ "aria-label": "Address Line 1" }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Address Line 2 (optional)"
          name="second_address_line"
          value={form.second_address_line}
          onChange={handleChange}
          sx={inputStyles}
          inputProps={{ "aria-label": "Address Line 2" }}
        />

        
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Autocomplete
            options={countryData}
            autoHighlight
            getOptionLabel={(option) => option.name}
            value={selectedCountry}
            onChange={(event, newValue) => handleCountrySelect(newValue)}
            sx={{ flex: 1, minWidth: 120 }}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span style={{ fontSize: "1.4rem" }}>{option.flag}</span>
                <Typography>{option.name}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Country"
                sx={inputStyles}
                inputProps={{ ...params.inputProps, "aria-label": "Country" }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedCountry ? (
                    <Box sx={{ mr: 1, fontSize: "1.4rem" }}>{selectedCountry.flag}</Box>
                  ) : null,
                }}
                error={Boolean(errors.country)}
                helperText={errors.country}
              />
            )}
          />

          <Autocomplete
            options={cityList}
            autoHighlight
            getOptionLabel={(option) => option}
            onChange={handleCitySelect}
            sx={{ flex: 1, minWidth: 120 }}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="City"
                error={Boolean(errors.city)}
                helperText={errors.city}
                sx={inputStyles}
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "City",
                }}
              />
            )}
          />
          <TextField
            fullWidth
            required
            label="Postal Code"
            name="postal_code"
            value={form.postal_code}
            onChange={handleChange}
            error={Boolean(errors.postal_code)}
            helperText={errors.postal_code}
            sx={{ ...inputStyles, flex: 1, minWidth: 100 }}
            inputProps={{ "aria-label": "Postal Code" }}
          />
        </Box>

        
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            required
            label="Date of Birth"
            name="dob"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.dob}
            onChange={handleChange}
            error={Boolean(errors.dob)}
            helperText={errors.dob}
            sx={inputStyles}
            inputProps={{ "aria-label": "Date of Birth" }}
          />
          <TextField
            fullWidth
            required
            label="Nationality"
            name="nationality"
            value={form.nationality}
            onChange={handleChange}
            error={Boolean(errors.nationality)}
            helperText={errors.nationality}
            sx={inputStyles}
            inputProps={{ "aria-label": "Nationality" }}
          />
        </Box>

        
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            required
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={form.password}
            onChange={(e) => {
              const value = e.target.value;
              handleChange(e);

              if (!validatePassword(value)) {
                setErrors((prev) => ({
                  ...prev,
                  password:
                    "Use at least 6 characters, one uppercase, one lowercase, and one number.",
                }));
              } else {
                setErrors((prev) => ({ ...prev, password: "" }));
              }

              if (form.confirm_password && form.confirm_password !== value) {
                setErrors((prev) => ({
                  ...prev,
                  confirm_password: "Passwords do not match.",
                }));
              } else if (form.confirm_password) {
                setErrors((prev) => ({ ...prev, confirm_password: "" }));
              }
            }}
            error={Boolean(errors.password)}
            helperText={errors.password || " "}
            sx={inputStyles}
            inputProps={{ "aria-label": "Password" }}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={toggleShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            required
            label="Confirm Password"
            name="confirm_password"
            type={showConfirmPassword ? "text" : "password"}
            value={form.confirm_password}
            onChange={(e) => {
              const value = e.target.value;
              handleChange(e);

              if (value !== form.password) {
                setErrors((prev) => ({
                  ...prev,
                  confirm_password: "Passwords do not match.",
                }));
              } else {
                setErrors((prev) => ({ ...prev, confirm_password: "" }));
              }
            }}
            error={Boolean(errors.confirm_password)}
            helperText={errors.confirm_password || " "}
            sx={inputStyles}
            inputProps={{ "aria-label": "Confirm Password" }}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    onClick={toggleShowConfirmPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
}